import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {css} from '@emotion/core'
import styled from '@emotion/styled'
import Settings from 'Settings'

import CompactHeader from '@daedalus/atlas/src/components/shared/general/CompactHeader'
import Overlay from '@daedalus/atlas/src/components/shared/general/Overlay'
import Text from '@daedalus/atlas/src/components/designSystem/Text'
import {MOBILE_HEADER_HEIGHT} from '@daedalus/atlas/src/components/shared/general/CompactHeader/constants'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@daedalus/core/src/auth/modules/actions'
import {updateUserMetadata} from '@daedalus/core/src/user/services'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import availableLanguages from '@daedalus/core/src/localization/services/locale'
import {selectIsAuthenticated} from '@daedalus/core/src/auth/modules/selectors'
import {useLogOut} from '@daedalus/core/src/auth/modules/useCognito'

import useParams from 'components/data/URLParams/useParams'
import UserSideMenuWithStore from 'components/UserSideMenuWithStore'
import PreferenceSelectorMobile from '@daedalus/atlas/src/components/shared/user/PreferenceSelector/Mobile'

import {
  splitPopularAndOtherCurrencies,
  getEnabledCurrencies
} from '@daedalus/core/src/localization/business/currency'

import {getPreferencesVisibility} from 'modules/overlay/selectors'
import {changeLanguage} from 'modules/localization/actions/changeLanguage'
import {changeCurrency} from 'modules/localization/actions/changeCurrency'
import {trackEventNew} from 'modules/analytics/actions'
import {
  EventName,
  EventCategory,
  EventAction
} from 'modules/analytics/actions/trackEventNew'
import {getPageSpace} from 'modules/analytics/trackEventNewHelpers'
import brand from 'configs/brand'
import {
  changeMembershipVisibility,
  changePreferencesVisibility
} from 'modules/overlay/actions'

type PropsType = {
  currentCurrency: string
  currentLanguage: string
  hasLanguageSelector: boolean
  hasCurrencySelector: boolean
}

const OverlaySection = styled('section')`
  height: calc(100% - ${MOBILE_HEADER_HEIGHT}px);
  display: flex;
  flex-direction: column;
`

const LogoutButton = styled('div')`
  margin-top: auto;
`

const HeaderWrapper = styled('div')(
  ({theme}) => css`
    position: relative;
    z-index: 2;

    box-shadow: ${theme.shadows.action};
  `
)

const PreferencesOverlay = ({
  currentCurrency,
  currentLanguage,
  hasLanguageSelector,
  hasCurrencySelector
}: PropsType) => {
  const dispatch = useDispatch()
  const urlParams = useParams()
  const space = getPageSpace(window.location.pathname)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const logOut = useLogOut()

  const handleLogoutClick = useCallback(() => logOut(), [logOut])

  const enabledCurrencies = getEnabledCurrencies(
    Settings.get('REACT_APP_ENABLED_CURRENCIES')
  )
  const {currenciesList, popularCurrenciesList} =
    splitPopularAndOtherCurrencies(enabledCurrencies)

  const handleHidePreferences = () =>
    dispatch(changePreferencesVisibility(false)())

  const handleShowMembership = () => {
    dispatch(changeMembershipVisibility(true)())
  }

  const onChangeLanguage = async language => {
    if (isAuthenticated) await updateUserMetadata({language})
    dispatch(
      trackEventNew({
        name: EventName.LANGUAGE_CHANGE,
        category: EventCategory.USER_ACTION,
        space,
        action: EventAction.CLICK,
        value: language,
        urlParams
      })
    )
    setTimeout(() => {
      dispatch(changeLanguage(language))
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }

  const onChangeCurrency = value => {
    dispatch(
      trackEventNew({
        name: EventName.CURRENCY_CHANGE,
        category: EventCategory.USER_ACTION,
        space,
        action: EventAction.CLICK,
        value,
        urlParams
      })
    )

    setTimeout(() => {
      dispatch(changeCurrency(value))
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }

  const isPreferencesVisible = Boolean(useSelector(getPreferencesVisibility))

  const overlayHeaderContent = (
    <FormattedMessageWrapper id="preferences" defaultMessage="Preferences" />
  )

  return (
    <Overlay isOpen={isPreferencesVisible}>
      <HeaderWrapper>
        <CompactHeader
          showBackButton
          brand={brand}
          onBackButtonClick={handleHidePreferences}
        >
          {overlayHeaderContent}
        </CompactHeader>
      </HeaderWrapper>
      <OverlaySection>
        <UserSideMenuWithStore />

        <PreferenceSelectorMobile
          availableLanguages={availableLanguages}
          currentLanguage={currentLanguage}
          currenciesList={currenciesList}
          popularCurrenciesList={popularCurrenciesList}
          currentCurrency={currentCurrency}
          hasLanguageSelector={hasLanguageSelector}
          hasCurrencySelector={hasCurrencySelector}
          onChangeCurrency={onChangeCurrency}
          onChangeLanguage={onChangeLanguage}
          onShowMembershipOverlay={handleShowMembership}
        />
        {isAuthenticated && (
          <LogoutButton onClick={handleLogoutClick}>
            <Text
              marginBottomSize="s700"
              align="center"
              appearance="subtitle1"
              colorPath="negativeColor"
            >
              <FormattedMessageWrapper
                dataId="LogOutProfileLink"
                id="userWidget.signOut"
                defaultMessage="Sign Out"
              />
            </Text>
          </LogoutButton>
        )}
      </OverlaySection>
    </Overlay>
  )
}

export default PreferencesOverlay
